import { useEffect, useState } from "react";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import IconX from "@images/pressClassification/icon-unsuitable-x-mark.svg";

import { UNSUITABLE_REASON_LIST_TITLE_BY_TYPE } from "./components/UnsuitableReasonCheckItem/constants/UnsuitableReason.constants";
import UnsuitableReasonCheckItem from "./components/UnsuitableReasonCheckItem/UnsuitableReasonCheckItem";
import * as S from "./UnsuitableReasonModalContainer.style";
import { UNSUITABLE_REASON_KEYS } from "../../constants/unsuitableReason.constants";
import { useHandleUnsuitablePress } from "../../hooks/useHandleUnsuitablePress";
import {
  UnsuitableReasonModalPosition,
  UnsuitableReasonUsageType,
} from "../../types/unsuitableReason.types";

interface Props {
  closeModalUnsuitablePress: () => void;
  isOpenModalUnsuitablePress: boolean;
  articleRejectReason: string | null;
  unsuitableReasonUsageType: UnsuitableReasonUsageType;
  draftArticleId: number;
  modalPosition?: UnsuitableReasonModalPosition;
}

const UnsuitableReasonModalContainer = ({
  closeModalUnsuitablePress,
  isOpenModalUnsuitablePress,
  articleRejectReason,
  unsuitableReasonUsageType,
  draftArticleId,
  modalPosition,
}: Props) => {
  const { data: articleInfo } = useArticleInfoQuery(Number(draftArticleId));
  const [isUpdated, setIsUpdated] = useState(false);
  const {
    unsuitablePressReasonList,
    setUnsuitablePressReasonList,
    targetUnsuitableReasonList,
    targetDefaultReasonList,
  } = useHandleUnsuitablePress({ type: unsuitableReasonUsageType });

  useEffect(() => {
    const updateResponseDataList = targetUnsuitableReasonList.map((item) => {
      if (
        articleInfo.isReviewRequired === true &&
        item.id === "S930-100" &&
        articleInfo.draftArticleStatusHighest !== DRAFT_STATUS_CODE.unsuitable
      ) {
        return {
          ...item,
          duplId: articleInfo.reviewIdList?.at(0),
          isTarget: true,
        };
      }
      if (articleInfo.isReviewRequired === false && item.id === "S930-100") {
        return {
          ...item,
          duplId: articleInfo.reviewIdList?.at(0),
          isTarget: false,
        };
      }
      if (
        item.reason === articleInfo.articleRejectReason &&
        !!articleInfo.articleRejectReason
      ) {
        if (item.type === "addId")
          return {
            ...item,
            duplId: articleInfo.duplArticleId,
            isTarget: true,
          };

        return {
          ...item,
          isTarget: true,
        };
      } else if (
        articleInfo.articleRejectReason &&
        !targetDefaultReasonList.includes(articleInfo.articleRejectReason)
      ) {
        if (item.type === "writing")
          return {
            ...item,
            reason: articleInfo.articleRejectReason,
            isTarget: true,
          };
      }

      return item;
    });

    setUnsuitablePressReasonList(updateResponseDataList);
    setIsUpdated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    articleInfo.articleRejectReason,
    setUnsuitablePressReasonList,
    articleInfo.isReviewRequired,
  ]);

  const isScroll =
    unsuitableReasonUsageType !== UNSUITABLE_REASON_KEYS.pressList &&
    unsuitableReasonUsageType !== UNSUITABLE_REASON_KEYS.ctgList;

  const leftContents = unsuitablePressReasonList.filter(
    ({ type }) => type === "default",
  );

  const rightContents = unsuitablePressReasonList.filter(
    ({ type }) => type !== "default",
  );

  return (
    <S.PressUnsuitableReasonContainer
      key={`unsuitable-reason-modal-container-${unsuitablePressReasonList}-${articleInfo.isReviewRequired}-${articleInfo.reviewId}-${articleInfo.draftArticleStatusHighest}-${articleInfo.articleRejectReason}`}
      $isScroll={isScroll}
      isOpenModalUnsuitablePress={isOpenModalUnsuitablePress}
      $modalPosition={modalPosition}
      columnCount={
        unsuitableReasonUsageType === UNSUITABLE_REASON_KEYS.pressDetail ? 2 : 1
      }
    >
      <S.TopWrapper>
        <S.Text>
          {UNSUITABLE_REASON_LIST_TITLE_BY_TYPE[unsuitableReasonUsageType]}
        </S.Text>
        <S.CloseButton onClick={closeModalUnsuitablePress}>
          <img src={IconX} alt="close button" />
        </S.CloseButton>
      </S.TopWrapper>
      {unsuitableReasonUsageType === UNSUITABLE_REASON_KEYS.pressDetail ? (
        <S.ReasonListRow>
          <S.ReasonListColumn>
            {isUpdated &&
              leftContents.map((item) => (
                <UnsuitableReasonCheckItem
                  key={item.id}
                  descriptionComponent={item.descriptionComponent}
                  draftArticleId={draftArticleId}
                  name="unsuitableReason"
                  reasonId={item.id}
                  reasonType={item.type}
                  isTarget={item.isTarget}
                  placeholder={item.placeholder}
                  reason={item.reason}
                  explanation={item.explanation}
                  duplId={item.duplId}
                  articleRejectReason={articleRejectReason}
                  unsuitablePressReasonList={unsuitablePressReasonList}
                  setUnsuitablePressReasonList={setUnsuitablePressReasonList}
                  unsuitableReasonUsageType={unsuitableReasonUsageType}
                  closeModalUnsuitablePress={closeModalUnsuitablePress}
                  reviewIdList={articleInfo.reviewIdList || null}
                />
              ))}
          </S.ReasonListColumn>
          <S.ReasonListColumn>
            {isUpdated &&
              rightContents.map((item) => (
                <UnsuitableReasonCheckItem
                  key={item.id}
                  descriptionComponent={item.descriptionComponent}
                  draftArticleId={draftArticleId}
                  name="unsuitableReason"
                  reasonId={item.id}
                  reasonType={item.type}
                  isTarget={item.isTarget}
                  placeholder={item.placeholder}
                  reason={item.reason}
                  explanation={item.explanation}
                  duplId={item.duplId}
                  articleRejectReason={articleRejectReason}
                  unsuitablePressReasonList={unsuitablePressReasonList}
                  setUnsuitablePressReasonList={setUnsuitablePressReasonList}
                  unsuitableReasonUsageType={unsuitableReasonUsageType}
                  closeModalUnsuitablePress={closeModalUnsuitablePress}
                  reviewIdList={articleInfo.reviewIdList || null}
                />
              ))}
          </S.ReasonListColumn>
        </S.ReasonListRow>
      ) : (
        <S.ReasonCheckListBox>
          {isUpdated &&
            unsuitablePressReasonList.map((item) => (
              <UnsuitableReasonCheckItem
                key={item.id}
                descriptionComponent={item.descriptionComponent}
                draftArticleId={draftArticleId}
                name="unsuitableReason"
                reasonId={item.id}
                reasonType={item.type}
                isTarget={item.isTarget}
                placeholder={item.placeholder}
                reason={item.reason}
                explanation={item.explanation}
                duplId={item.duplId}
                articleRejectReason={articleRejectReason}
                unsuitablePressReasonList={unsuitablePressReasonList}
                setUnsuitablePressReasonList={setUnsuitablePressReasonList}
                unsuitableReasonUsageType={unsuitableReasonUsageType}
                closeModalUnsuitablePress={closeModalUnsuitablePress}
                reviewIdList={articleInfo.reviewIdList || null}
              />
            ))}
        </S.ReasonCheckListBox>
      )}
    </S.PressUnsuitableReasonContainer>
  );
};

export default UnsuitableReasonModalContainer;
